import React, { useContext, useEffect, useState } from 'react';
import './ChampionRuneBuild.css';
import { 
  RuneStats, 
  RUNE_TREES, 
  SHARD_PATHS, 
  ShardPathKeys, 
  styleToTreeKey
} from '../../utils/runeConfig';
import { 
  getRuneIdByName, 
  isRuneSelected,
  parsePerkIds,
  runeIdMapping
} from '../../utils/runeIdMapping';
import GameAssetsContext from '../../../XXX/context/GameAssetContext';
import { getRuneImageUrlByName } from '../../../utils/runeImageMapping';

interface Props {
  championName: string;
}

const ChampionRuneBuild: React.FC<Props> = ({ championName }) => {
  const { gameWideAssets } = useContext(GameAssetsContext);
  const [loading, setLoading] = useState(true);
  const [runeData, setRuneData] = useState<RuneStats[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Updated rune image URL function to use shared utility
  const getUpdatedRuneImageUrl = (runeName: string, runeId?: number): string => {
    return getRuneImageUrlByName(
      runeName, 
      gameWideAssets.runeIcons || {}, 
      runeId && runeIdMapping[runeId]?.path ? 
        `https://ddragon.leagueoflegends.com/cdn/img/perk-images/${runeIdMapping[runeId].path}` : 
        gameWideAssets.runes?.[runeName]
    );
  };
  
  useEffect(() => {
    const fetchRuneData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/stats/champion/${championName}/runes`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch rune data: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Parse string IDs into arrays of numbers
        const processedData = data.map((runeSet: RuneStats) => {
          return {
            ...runeSet,
            primary_perk_ids_array: parsePerkIds(runeSet.primary_perk_ids),
            secondary_perk_ids_array: parsePerkIds(runeSet.secondary_perk_ids)
          };
        });
        
        setRuneData(processedData);
        console.log('Fetched rune data:', processedData);
      } catch (err) {
        console.error('Error fetching rune data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    if (championName) {
      fetchRuneData();
    }
  }, [championName]);
  
  if (loading) {
    return <div className="rb-container">Loading champion rune data...</div>;
  }
  
  if (error) {
    return <div className="rb-container">Error: {error}</div>;
  }
  
  // Get the most popular rune set
  const mostPopularRuneSet = runeData.length > 0 ? runeData[0] : null;
  
  if (!mostPopularRuneSet) {
    return <div className="rb-container">No rune data available for {championName}</div>;
  }
  
  // Get tree keys for primary and secondary styles
  const primaryTreeKey = styleToTreeKey[mostPopularRuneSet.primary_style.toLowerCase()];
  const secondaryTreeKey = styleToTreeKey[mostPopularRuneSet.secondary_style.toLowerCase()];
  
  // Render primary rune tree
  const renderPrimaryRunePath = (path: string) => {
    const runeData = RUNE_TREES[path];
    
    if (!runeData) {
      return <div>No rune data found for {path}</div>;
    }
    
    return (
      <>
        <div className="rb-keystone-row">
          {runeData.KEYSTONES.map((keystone, index) => {
            const isSelected = isRuneSelected(keystone, mostPopularRuneSet.primary_perk_ids_array || []);
            const runeId = getRuneIdByName(keystone);
            
            return (
              <div
                key={index}
                className={`rb-rune-slot ${isSelected ? 'rb-selected' : 'rb-unselected'}`}
              >
                <img 
                  src={getUpdatedRuneImageUrl(keystone, runeId)}
                  alt={keystone} 
                />
              </div>
            );
          })}
        </div>
        <div className="rb-slot1-row">
          {runeData.SLOT1.map((rune, index) => {
            const isSelected = isRuneSelected(rune, mostPopularRuneSet.primary_perk_ids_array || []);
            const runeId = getRuneIdByName(rune);
            
            return (
              <div
                key={index}
                className={`rb-rune-slot ${isSelected ? 'rb-selected' : 'rb-unselected'}`}
              >
                <img 
                  src={getUpdatedRuneImageUrl(rune, runeId)}
                  alt={rune} 
                />
              </div>
            );
          })}
        </div>
        <div className="rb-slot2-row">
          {runeData.SLOT2.map((rune, index) => {
            const isSelected = isRuneSelected(rune, mostPopularRuneSet.primary_perk_ids_array || []);
            const runeId = getRuneIdByName(rune);
            
            return (
              <div
                key={index}
                className={`rb-rune-slot ${isSelected ? 'rb-selected' : 'rb-unselected'}`}
              >
                <img 
                  src={getUpdatedRuneImageUrl(rune, runeId)}
                  alt={rune} 
                />
              </div>
            );
          })}
        </div>
        <div className="rb-slot3-row">
          {runeData.SLOT3.map((rune, index) => {
            const isSelected = isRuneSelected(rune, mostPopularRuneSet.primary_perk_ids_array || []);
            const runeId = getRuneIdByName(rune);
            
            return (
              <div
                key={index}
                className={`rb-rune-slot ${isSelected ? 'rb-selected' : 'rb-unselected'}`}
              >
                <img 
                  src={getUpdatedRuneImageUrl(rune, runeId)}
                  alt={rune} 
                />
              </div>
            );
          })}
        </div>
      </>
    );
  };
  
  // Render secondary rune tree
  const renderSecondaryRunePath = (path: string) => {
    const runeData = RUNE_TREES[path];
    
    if (!runeData) {
      return <div>No rune data found for {path}</div>;
    }
    
    return (
      <>
        <div className="rb-slot1-row">
          {runeData.SLOT1.map((rune, index) => {
            const isSelected = isRuneSelected(rune, mostPopularRuneSet.secondary_perk_ids_array || []);
            const runeId = getRuneIdByName(rune);
            
            return (
              <div
                key={index}
                className={`rb-rune-slot ${isSelected ? 'rb-selected' : 'rb-unselected'}`}
              >
                <img 
                  src={getUpdatedRuneImageUrl(rune, runeId)}
                  alt={rune} 
                />
              </div>
            );
          })}
        </div>
        <div className="rb-slot2-row">
          {runeData.SLOT2.map((rune, index) => {
            const isSelected = isRuneSelected(rune, mostPopularRuneSet.secondary_perk_ids_array || []);
            const runeId = getRuneIdByName(rune);
            
            return (
              <div
                key={index}
                className={`rb-rune-slot ${isSelected ? 'rb-selected' : 'rb-unselected'}`}
              >
                <img 
                  src={getUpdatedRuneImageUrl(rune, runeId)}
                  alt={rune} 
                />
              </div>
            );
          })}
        </div>
        <div className="rb-slot3-row">
          {runeData.SLOT3.map((rune, index) => {
            const isSelected = isRuneSelected(rune, mostPopularRuneSet.secondary_perk_ids_array || []);
            const runeId = getRuneIdByName(rune);
            
            return (
              <div
                key={index}
                className={`rb-rune-slot ${isSelected ? 'rb-selected' : 'rb-unselected'}`}
              >
                <img 
                  src={getUpdatedRuneImageUrl(rune, runeId)}
                  alt={rune} 
                />
              </div>
            );
          })}
        </div>
      </>
    );
  };
  
  // Render stat shards
  const renderShardRow = (rowKey: ShardPathKeys) => {
    const shards = SHARD_PATHS[rowKey];
    
    // Map the backend shard names to our frontend keys
    const selectedShard = rowKey === 'ROW1' ? mostPopularRuneSet.shard_offense : 
                         rowKey === 'ROW2' ? mostPopularRuneSet.shard_flex : 
                         mostPopularRuneSet.shard_defense;
    
    // Map the API shard name to our UI keys
    const mapShardName = (apiShardName: string): string => {
      const lowerCaseName = apiShardName.toLowerCase();
      if (lowerCaseName.includes('adaptive')) return 'adaptiveForce';
      if (lowerCaseName.includes('attack')) return 'attackSpeed';
      if (lowerCaseName.includes('ability') || lowerCaseName.includes('haste')) return 'abilityHaste';
      if (lowerCaseName.includes('armor')) return 'armor';
      if (lowerCaseName.includes('magic')) return 'magicResist';
      if (lowerCaseName.includes('scaling')) return 'healthScaling';
      if (lowerCaseName.includes('health') && !lowerCaseName.includes('scaling')) return 'health';
      if (lowerCaseName.includes('movement') || lowerCaseName.includes('speed')) return 'moveSpeed';
      if (lowerCaseName.includes('tenacity')) return 'tenacity';
      return apiShardName;
    };
    
    const mappedSelectedShard = mapShardName(selectedShard);
    
    return (
      <div className="rb-shard-row">
        {shards.map(shard => {
          // Get the correct path segment based on shard type
          let pathSegment: string;
          switch (shard.toLowerCase()) {
            case 'adaptiveforce':
              pathSegment = 'statmodsadaptiveforceicon.png';
              break;
            case 'attackspeed':
              pathSegment = 'statmodsattackspeedicon.png';
              break;
            case 'abilityhaste':
              pathSegment = 'statmodscdrscalingicon.png';
              break;
            case 'movespeed':
              pathSegment = 'statmodsmovementspeedicon.png';
              break;
            case 'healthscaling':
              pathSegment = 'statmodshealthplusicon.png';
              break;
            case 'health':
              pathSegment = 'statmodshealthscalingicon.png';
              break;
            case 'tenacity':
              pathSegment = 'statmodstenacityicon.png';
              break;
            default:
              pathSegment = `statmods${shard.toLowerCase()}icon.png`;
          }
          
          const imageUrl = `https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/${pathSegment}`;
          
          return (
            <div
              key={shard}
              className={`rb-shard-slot ${mappedSelectedShard === shard ? 'rb-selected' : 'rb-unselected'}`}
            >
              <img src={imageUrl} alt={shard} />
            </div>
          );
        })}
      </div>
    );
  };
  
  // Get the tree image URL
  const getTreeImageUrl = (style: string): string => {
    const lowerCaseStyle = style.toLowerCase();
    return `https://ddragon.canisback.com/img/perk-images/Styles/${
      lowerCaseStyle === 'precision' ? '7201_Precision.png' :
      lowerCaseStyle === 'domination' ? '7200_Domination.png' :
      lowerCaseStyle === 'sorcery' ? '7202_Sorcery.png' :
      lowerCaseStyle === 'resolve' ? '7204_Resolve.png' :
      lowerCaseStyle === 'inspiration' ? '7203_Whimsy.png' :
      ''
    }`;
  };
  
  return (
    <div className="rb-container">
      <h2 className="rb-title">Runes</h2>
      <div className="rb-trees-container">
        {/* Primary Tree */}
        <div className="rb-tree">
          <div className="rb-tree-header">
            <img 
              src={getTreeImageUrl(mostPopularRuneSet.primary_style)} 
              alt={mostPopularRuneSet.primary_style} 
              className="rb-tree-icon"
            />
            <h3 className="rb-tree-name">{mostPopularRuneSet.primary_style}</h3>
          </div>
          
          <div className="rb-primary-path">
            {renderPrimaryRunePath(primaryTreeKey)}
          </div>
        </div>
        
        {/* Secondary Tree */}
        <div className="rb-tree">
          <div className="rb-tree-header">
            <img 
              src={getTreeImageUrl(mostPopularRuneSet.secondary_style)} 
              alt={mostPopularRuneSet.secondary_style} 
              className="rb-tree-icon"
            />
            <h3 className="rb-tree-name">{mostPopularRuneSet.secondary_style}</h3>
          </div>
          
          <div className="rb-secondary-path">
            {renderSecondaryRunePath(secondaryTreeKey)}
          </div>
          
          {/* Stat Shards */}
          <div className="rb-shards">
            <div className="rb-shards-paths">
              {Object.keys(SHARD_PATHS).map(rowKey => renderShardRow(rowKey as ShardPathKeys))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChampionRuneBuild; 